import { useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import AuthContext from "contexte/AuthContext";
import { launchError, launchSuccess } from "components/notifications";
import { cancelUserBonus, fetchUserBonuses, grantUserBonus } from "services/userServices";
import { formatNumberWithSpaces } from "utils/numbers";
import './userBonuses.css'

const STATUS_LABELS = {
  active: 'Actif',
  used: 'Utilisé',
  expired: 'Expiré',
  cancelled: 'Annulé',
}

const MOVEMENT_LABELS = {
  GRANT: 'Bonus donné',
  OPEN: 'Ordre payé',
  REFUND: 'Remboursé (ordre annulé ou partiel)',
  RETURN: 'Rendu à la clôture',
  CANCEL: 'Reste annulé',
}

const REASON_MAX_LENGTH = 255

// Dates stockées en UTC : un bonus expire à 23 h 59 UTC du jour choisi
const formatUtcDate = (value) => value ? new Date(value).toLocaleDateString('fr-FR', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' }) : ''
const formatUtcDateTime = (value) => value ? new Date(value).toLocaleString('fr-FR', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' UTC' : ''
const formatEuros = (value) => `${formatNumberWithSpaces(value)} €`
const todayUtc = () => new Date().toISOString().slice(0, 10)

const emptyForm = { amount: '', expirationDate: '', reason: '' }

// '' ou plus de 2 décimales : montant refusé, comme côté API
const isValidAmount = (value) => value !== '' && Number(value) > 0 && Math.round(Number(value) * 100) === Number(value) * 100

// Bonus du client (freebets sur le levier forex et métaux). Cachés à la compliance (rôle 3), seul le rôle 1 en donne ou en annule.
const UserBonuses = ({ user, id }) => {

  const { t } = useTranslation();
  const { verifyUser } = useContext(AuthContext);
  const role = verifyUser()?.role;

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  const load = async () => {
    try {
      setData(await fetchUserBonuses(user, id));
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
    }
  }

  useEffect(() => {
    let cancelled = false;
    fetchUserBonuses(user, id)
      .then((result) => { if (!cancelled) setData(result) })
      .catch(() => { if (!cancelled) setLoadError(true) });
    return () => { cancelled = true };
  }, [user, id]);

  const set = (field) => (e) => {
    setConfirming(false);
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  const formErrors = {
    amount: form.amount !== '' && !isValidAmount(form.amount) ? 'Montant supérieur à 0 €, 2 décimales au plus.' : null,
    expirationDate: form.expirationDate && form.expirationDate < todayUtc() ? "La date d'expiration ne peut pas être passée." : null,
  }
  const canSubmit = isValidAmount(form.amount) && form.expirationDate && !formErrors.expirationDate && form.reason.trim() && !submitting;

  const grant = async () => {
    setSubmitting(true);
    try {
      // midi UTC du jour choisi : l'API ramène l'expiration à 23 h 59 UTC de ce jour
      await grantUserBonus(user, id, { amount: Number(form.amount), expiration_date: `${form.expirationDate}T12:00:00.000Z`, reason: form.reason.trim() });
      launchSuccess(t('notifications.bonuses.grant.success'));
      setForm(emptyForm);
      setConfirming(false);
      await load();
    } catch (error) {
      const code = error?.response?.data?.error;
      launchError(t([`notifications.bonuses.grant.${code}`, 'notifications.bonuses.grant.error']));
    } finally {
      setSubmitting(false);
    }
  }

  const cancel = async (bonus) => {
    if (!window.confirm(`Annuler ce bonus ? Les ${formatEuros(bonus.remaining_amount)} restants seront perdus pour le client. Les positions déjà ouvertes avec ce bonus ne changent pas.`))
      return;
    setCancellingId(bonus.id);
    try {
      await cancelUserBonus(user, id, bonus.id);
      launchSuccess(t('notifications.bonuses.cancel.success'));
      await load();
    } catch (error) {
      const code = error?.response?.data?.error;
      launchError(t([`notifications.bonuses.cancel.${code}`, 'notifications.bonuses.cancel.error']));
    } finally {
      setCancellingId(null);
    }
  }

  return <div className='user_bonuses_main_div'>
    <div className='user_bonuses_header'>
      <p className='user_bonuses_title'>Bonus :</p>
      {data && <p className='user_bonuses_total'><span className='user_bonuses_label'>Disponible : </span>{formatEuros(data.total)}</p>}
    </div>
    <p className='user_bonuses_hint'>Utilisables sur une position levier forex ou métaux (LMAX) quand le client choisit d'utiliser son bonus à l'ouverture : il paie la marge et les frais d'ouverture avant le portefeuille. À la fermeture, le bonus est rendu moins la perte éventuelle (les frais d'ouverture restent payés) et seul le gain va au portefeuille.</p>

    {loadError && <p className='user_bonuses_error'>Impossible de charger les bonus. <button className='user_bonuses_link_button' onClick={load}>Réessayer</button></p>}
    {data && !data.bonuses.length && <p className='user_bonuses_empty'>Aucun bonus.</p>}

    {data?.bonuses.length > 0 &&
      <div className='user_bonuses_table_wrapper'>
        <table className='user_bonuses_table'>
          <thead>
            <tr>
              <th scope='col'>Donné le</th>
              <th scope='col'>Origine</th>
              <th scope='col' className='user_bonuses_number'>Montant</th>
              <th scope='col' className='user_bonuses_number'>Reste</th>
              <th scope='col'>Expire le</th>
              <th scope='col'>Statut</th>
              <th scope='col'><span className='user_bonuses_visually_hidden'>Détail et actions</span></th>
            </tr>
          </thead>
          <tbody>
            {data.bonuses.map((bonus) => (
              <tr key={bonus.id}>
                <td>{formatUtcDate(bonus.created_at)}</td>
                <td>{bonus.source === 'campaign' ? 'Campagne' : <>Manuel<span className='user_bonuses_reason'>{bonus.reason}</span></>}</td>
                <td className='user_bonuses_number'>{formatEuros(bonus.amount)}</td>
                <td className='user_bonuses_number'>{formatEuros(bonus.remaining_amount)}</td>
                <td>{formatUtcDate(bonus.expiration_date)}</td>
                <td><span className={`user_bonuses_status user_bonuses_status_${bonus.status}`}>{STATUS_LABELS[bonus.status] || bonus.status}</span></td>
                <td>
                  <details className='user_bonuses_details'>
                    <summary>Mouvements ({bonus.movements.length})</summary>
                    <ul>
                      {bonus.movements.map((movement, index) => (
                        <li key={index}>
                          <span>{formatUtcDateTime(movement.created_at)}</span>
                          <span>{MOVEMENT_LABELS[movement.type] || movement.type}{movement.client_order_id && ` · ${movement.client_order_id}`}</span>
                          <span className='user_bonuses_number'>{movement.amount > 0 ? '+' : ''}{formatEuros(movement.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                  {role === 1 && bonus.status === 'active' &&
                    <button className='user_bonuses_link_button user_bonuses_cancel' disabled={cancellingId === bonus.id} onClick={() => cancel(bonus)}>
                      {cancellingId === bonus.id ? 'Annulation…' : 'Annuler'}
                    </button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    }

    {role === 1 &&
      <div className='user_bonuses_form'>
        <p className='user_bonuses_subtitle'>Donner un bonus</p>
        <div className='user_bonuses_form_row'>
          <div className='user_bonuses_field'>
            <label htmlFor='bonus-amount'>Montant (€)</label>
            <input id='bonus-amount' type='number' inputMode='decimal' min='0.01' step='0.01' value={form.amount} onChange={set('amount')}
              aria-invalid={formErrors.amount ? true : undefined} aria-describedby={formErrors.amount ? 'bonus-amount-error' : undefined} />
            {formErrors.amount && <p id='bonus-amount-error' className='user_bonuses_error'>{formErrors.amount}</p>}
          </div>
          <div className='user_bonuses_field'>
            <label htmlFor='bonus-expiration'>Expire le (23 h 59 UTC)</label>
            <input id='bonus-expiration' type='date' min={todayUtc()} value={form.expirationDate} onChange={set('expirationDate')}
              aria-invalid={formErrors.expirationDate ? true : undefined} aria-describedby={formErrors.expirationDate ? 'bonus-expiration-error' : undefined} />
            {formErrors.expirationDate && <p id='bonus-expiration-error' className='user_bonuses_error'>{formErrors.expirationDate}</p>}
          </div>
        </div>
        <div className='user_bonuses_field'>
          <label htmlFor='bonus-reason'>Raison</label>
          <textarea id='bonus-reason' rows={2} maxLength={REASON_MAX_LENGTH} value={form.reason} onChange={set('reason')} aria-describedby='bonus-reason-hint' />
          <p id='bonus-reason-hint' className='user_bonuses_hint'>Obligatoire, visible uniquement dans le back office : c'est la seule trace de pourquoi ce bonus a été donné.</p>
        </div>
        {!confirming
          ? <div><button className='user_action_button' disabled={!canSubmit} onClick={() => setConfirming(true)}>Donner le bonus</button></div>
          : <div className='user_bonuses_confirm' role='alert'>
            <p>Donner <strong>{formatEuros(Number(form.amount))}</strong> de bonus à ce client, utilisable jusqu'au <strong>{formatUtcDate(`${form.expirationDate}T12:00:00.000Z`)}</strong> ?</p>
            <div className='user_bonuses_confirm_actions'>
              <button className='user_action_button' disabled={submitting} aria-busy={submitting || undefined} onClick={grant}>{submitting ? 'Envoi…' : 'Confirmer'}</button>
              <button className='user_bonuses_link_button' disabled={submitting} onClick={() => setConfirming(false)}>Modifier</button>
            </div>
          </div>}
      </div>
    }
  </div>
}

export default UserBonuses
